import {
  ArrowLeft, Ban, Link2, MoreHorizontal, Pause, Pencil, PhoneCall, PhoneOff, Play, RotateCcw,
  StickyNote, Tag, Undo2,
} from "lucide-react";
import * as React from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import { Page } from "@/components/layout/page";
import { ConfirmDialog } from "@/components/ui/alert-dialog";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardHeader, DescriptionItem, DescriptionList } from "@/components/ui/card";
import {
  Dialog, DialogBody, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { EmptyState, ErrorState, Spinner, TableSkeleton } from "@/components/ui/feedback";
import { Field } from "@/components/ui/field";
import { Input } from "@/components/ui/input";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/misc";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { useToast } from "@/components/ui/toast";
import { BlockCallerDialog } from "@/features/block-caller";
import {
  CallPager, DirectionIcon, MediaCell, MediaHead,
} from "@/features/call-report";
import { FollowUpCard } from "@/features/contact-follow-up";
import { ContactFormDialog } from "@/features/contact-form";
import { ContactOutcomeDialog } from "@/features/contact-outcome";
import { ContactNotes, ContactTimeline } from "@/features/contact-timeline";
import { ContactTranscripts } from "@/features/contact-transcripts";
import { ApiError } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { untilPhrase } from "@/lib/blocked-callers";
import { useBlockOn } from "@/lib/blocked-callers-api";
import { cadenceNextLine, priorityReasonText } from "@/lib/cadence";
import { callOutcome, talkShown } from "@/lib/call-outcome";
import {
  contactName, dialDetail, dialText, nextText, reopenStages, reopenSummary, stageText,
} from "@/lib/contacts";
import { languageName, localTime } from "@/lib/lead-card";
import { isOutsideNumber } from "@/lib/phone-number";
import {
  CALLS_PAGE_SIZE, useContact, useContactCalls, useContactOutcome, useFirms, useLeadCadence,
  useLeadCadenceAction, useReopenLead, useUpdateContact,
} from "@/lib/queries";
import {
  atLeast, type Cdr, type LeadCard, type LeadCardCampaign,
} from "@/lib/types";
import { useNow } from "@/lib/use-now";
import { formatDateTime, formatDuration, formatPhoneNumber, relativeTime } from "@/lib/utils";
import { CallDetailDialog } from "@/routes/calls";
import { DialNumberButton } from "@/softphone/dial-number";

/**
 * The contact record (ADR-044): header, facts on the left, the timeline on the right.
 *
 * Everything a rep typed lives in a table of its own; nothing here writes the dialer's columns. Who
 * may open it is the card's rule (ADR-024) widened to the campaigns whose lines a rep staffs — out of
 * scope is a 404, as it is for the card. The tab is in the URL (`?tab=calls`), so a link lands where
 * the reader was.
 */

const TABS = ["timeline", "calls", "transcripts", "lists", "notes"] as const;
type Tab = (typeof TABS)[number];

/** Same-phone records listed on the record itself; past that, the contacts list searched by the phone. */
const SIBLINGS_SHOWN = 5;

/** The attributes worth a line of their own, when the source sent them. */
const SOURCE_ATTRIBUTES: [string, string][] = [
  ["ghl_tags", "GHL tags"],
  ["line_type", "Line type"],
  ["business_name", "Business"],
  ["utm_source", "UTM source"],
  ["utm_medium", "UTM medium"],
  ["utm_campaign", "UTM campaign"],
  ["trustedform_url", "TrustedForm"],
];

export function ContactPage() {
  const { leadId } = useParams();
  const [params, setParams] = useSearchParams();
  const requested = params.get("tab") ?? "";
  const tab: Tab = (TABS as readonly string[]).includes(requested) ? (requested as Tab) : "timeline";
  const { data: contact, isPending, error, refetch } = useContact(leadId);

  if (isPending) {
    return (
      <Page>
        <div className="flex min-h-[40vh] items-center justify-center">
          <Spinner className="size-5" />
        </div>
      </Page>
    );
  }
  if (error || !contact) {
    const missing = error instanceof ApiError && error.status === 404;
    return (
      <Page>
        {missing ? (
          <EmptyState
            className="min-h-[60vh]"
            title="Contact not found"
            description="It may belong to another organisation, or to a campaign you do not work."
            action={
              <Button variant="outline" asChild>
                <Link to="/contacts">Back to contacts</Link>
              </Button>
            }
          />
        ) : (
          <ErrorState
            className="min-h-[60vh]"
            error={error}
            title="Could not load the contact"
            action={
              <Button variant="outline" onClick={() => void refetch()}>
                Try again
              </Button>
            }
          />
        )}
      </Page>
    );
  }

  return (
    <Page>
      <Link to="/contacts" className="mb-3 inline-flex items-center gap-1 text-xs text-text-muted hover:text-text">
        <ArrowLeft className="size-3.5" />
        Contacts
      </Link>
      <Header contact={contact} />
      <div className="mt-5 grid gap-5 lg:grid-cols-[minmax(0,22rem)_minmax(0,1fr)]">
        <Facts contact={contact} />
        <Card className="overflow-hidden">
          <Tabs
            value={tab}
            onValueChange={(next) => {
              const p = new URLSearchParams(params);
              if (next === "timeline") p.delete("tab");
              else p.set("tab", next);
              setParams(p, { replace: true });
            }}
          >
            <TabsList className="px-4 pt-2">
              <TabsTrigger value="timeline">Timeline</TabsTrigger>
              <TabsTrigger value="calls">Calls</TabsTrigger>
              <TabsTrigger value="transcripts">Transcripts</TabsTrigger>
              <TabsTrigger value="lists">Lists {contact.campaigns.length ? <span className="ml-1 text-text-subtle">{contact.campaigns.length}</span> : null}</TabsTrigger>
              <TabsTrigger value="notes">Notes</TabsTrigger>
            </TabsList>
            <TabsContent value="timeline" className="p-4">
              <ContactTimeline contact={contact} composeFocus={params.get("compose") === "1"} />
            </TabsContent>
            <TabsContent value="calls">
              <ContactCalls contact={contact} />
            </TabsContent>
            <TabsContent value="transcripts">
              <ContactTranscripts contact={contact} />
            </TabsContent>
            <TabsContent value="lists">
              <ContactLists contact={contact} />
            </TabsContent>
            <TabsContent value="notes" className="p-4">
              <ContactNotes contact={contact} />
            </TabsContent>
          </Tabs>
        </Card>
      </div>
    </Page>
  );
}

function Header({ contact }: { contact: LeadCard }) {
  const { user } = useAuth();
  const supervisor = atLeast(user?.role, "supervisor");
  const admin = atLeast(user?.role, "admin");
  const toast = useToast();
  const now = useNow(30_000);
  const [params, setParams] = useSearchParams();
  const [editing, setEditing] = React.useState(false);
  const [outcome, setOutcome] = React.useState(false);
  const [dnc, setDnc] = React.useState(false);
  const [settingFirm, setSettingFirm] = React.useState(false);
  const [reopening, setReopening] = React.useState(false);
  const [blocking, setBlocking] = React.useState(false);
  const record = useContactOutcome();
  // Whether the switch turns this number away now (ADR-159): said beside Do not call, which is
  // the other half of "we do not talk to them" and stops only our own calls and texts.
  const blockable = isOutsideNumber(contact.phone);
  const block = useBlockOn(blockable ? { number: contact.phone, hidden: false } : null);
  const cadence = useLeadCadence(contact.id);
  const onCadence = (cadence.data?.memberships ?? []).filter((m) => m.cadence);
  const firstCadence = onCadence[0];
  const [cadenceAction, setCadenceAction] = React.useState<"pause" | "resume" | "re-enroll" | null>(null);
  const name = contactName(contact);
  const stage = stageText(contact.stage);
  const there = localTime(contact.timezone, now);
  const calling = contact.calling_campaign
    ? contact.campaigns.find((c) => c.campaign_id === contact.calling_campaign?.id) ?? null
    : null;
  const next = calling ? nextText({ dial: calling.dial ?? null, next_at: calling.next_at ?? null }, now) : null;

  const composeNote = () => {
    const p = new URLSearchParams(params);
    p.delete("tab");
    p.set("compose", "1");
    setParams(p, { replace: true });
  };

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(window.location.href);
      toast.success("Link copied");
    } catch {
      toast.error("Could not copy", "Copy the address bar instead.");
    }
  };

  return (
    <div className="flex flex-wrap items-start justify-between gap-4">
      <div className="min-w-0 space-y-1.5">
        <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
          <h1 className="text-xl font-semibold tracking-tight text-text">{name ?? "No name"}</h1>
          <span className="font-mono text-sm text-text-muted">
            <DialNumberButton number={contact.phone} name={name} />
          </span>
          {contact.firm ? (
            <Badge tone="neutral">{contact.firm.label}</Badge>
          ) : (
            <Badge tone="warning">No firm</Badge>
          )}
          {contact.dnc && <Badge tone="danger">Do not call</Badge>}
          {block && (
            <Badge
              tone="danger"
              title={[block.blocked_by ? `Blocked by ${block.blocked_by}` : null, block.note].filter(Boolean).join(". ") || undefined}
            >
              Calls blocked · {untilPhrase(block)}
            </Badge>
          )}
        </div>
        <p className="text-sm text-text-muted">
          {[
            contact.state ? `${contact.state}` : null,
            there ? `${there} there` : null,
            languageName(contact.language),
          ].filter(Boolean).join(" · ") || "No location on file"}
        </p>
        <div className="flex flex-wrap items-center gap-2 text-xs">
          <Badge tone={stage.tone} title={stage.description}>{stage.label}</Badge>
          {contact.stage_since && <span className="text-text-muted">since {relativeTime(contact.stage_since)}</span>}
          {contact.consent_captured_at ? (
            <Badge tone="success" title={contact.consent_wording ?? undefined}>
              Consent on {formatDateTime(contact.consent_captured_at).split(",").slice(0, 2).join(",")}
            </Badge>
          ) : (
            <Badge tone="outline" title="The dialer will not call a lead without consent evidence">No consent evidence</Badge>
          )}
          {firstCadence ? (
            <span className="text-text-muted" title={firstCadence.priority_reason ? `Priority ${firstCadence.priority}: ${priorityReasonText(firstCadence.priority_reason)}` : undefined}>
              Next: {inSentence(cadenceNextLine(firstCadence, now))} on {firstCadence.campaign_name}
            </span>
          ) : calling ? (
            <span className="text-text-muted">
              Next: {next ? `${next.toLowerCase()} on ${contact.calling_campaign?.name}` : `${dialText(calling.dial ?? null).label.toLowerCase()} on ${contact.calling_campaign?.name}`}
            </span>
          ) : null}
        </div>
      </div>
      {/* Not shrink-0: on a phone the group is wider than the page, and only a group that may
          shrink lets its buttons wrap instead of running under the edge. */}
      <div className="flex flex-wrap items-center gap-2">
        <Button variant="outline" size="sm" onClick={composeNote}>
          <StickyNote />
          Add note
        </Button>
        {admin && contact.reopen_stage && (
          <Button variant="outline" size="sm" onClick={() => setReopening(true)}>
            <Undo2 />
            Reopen
          </Button>
        )}
        <Button variant="outline" size="sm" onClick={() => setOutcome(true)}>
          <PhoneCall />
          Set outcome
        </Button>
        {!contact.dnc && (
          <Button variant="outline" size="sm" onClick={() => setDnc(true)}>
            <Ban />
            Do not call
          </Button>
        )}
        <Button variant="outline" size="sm" onClick={() => setEditing(true)}>
          <Pencil />
          Edit
        </Button>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="outline" size="icon" aria-label="More">
              <MoreHorizontal />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            {supervisor && (
              <DropdownMenuItem onSelect={() => setSettingFirm(true)}>
                <Tag />
                Set firm
              </DropdownMenuItem>
            )}
            {supervisor && onCadence.some((m) => m.held_by !== "cadence:paused" && !["done", "dnc", "exhausted", "retired", "blocked"].includes(m.state)) && (
              <DropdownMenuItem onSelect={() => setCadenceAction("pause")}>
                <Pause />
                Pause follow-up
              </DropdownMenuItem>
            )}
            {supervisor && onCadence.some((m) => m.held_by === "cadence:paused") && (
              <DropdownMenuItem onSelect={() => setCadenceAction("resume")}>
                <Play />
                Resume follow-up
              </DropdownMenuItem>
            )}
            {supervisor && !contact.dnc && onCadence.some((m) => ["exhausted", "done", "retired"].includes(m.state)) && (
              <DropdownMenuItem onSelect={() => setCadenceAction("re-enroll")}>
                <RotateCcw />
                Start follow-up again
              </DropdownMenuItem>
            )}
            {blockable && (
              <DropdownMenuItem onSelect={() => setBlocking(true)}>
                <PhoneOff />
                {block ? "Calls blocked" : "Block caller"}
              </DropdownMenuItem>
            )}
            <DropdownMenuItem onSelect={() => void copyLink()}>
              <Link2 />
              Copy link
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>

      <ContactFormDialog open={editing} onOpenChange={setEditing} contact={contact} />
      <ContactOutcomeDialog open={outcome} onOpenChange={setOutcome} contact={contact} />
      <SetFirmDialog open={settingFirm} onOpenChange={setSettingFirm} contact={contact} />
      <CadenceActionDialog action={cadenceAction} onClose={() => setCadenceAction(null)} contact={contact} />
      <ReopenDialog open={reopening} onOpenChange={setReopening} contact={contact} />
      <BlockCallerDialog
        open={blocking}
        onOpenChange={setBlocking}
        target={{ number: contact.phone, hidden: false, leadId: contact.id, name }}
      />
      <ConfirmDialog
        open={dnc}
        onOpenChange={setDnc}
        title="Mark do-not-call?"
        description="Every record with this phone is marked, every list drops them, and an admin cannot undo it. The outcome is recorded as Do not call."
        confirmLabel="Mark do-not-call"
        destructive
        loading={record.isPending}
        onConfirm={() =>
          record.mutate(
            { leadId: contact.id, disposition: "dnc" },
            {
              onSuccess: () => {
                setDnc(false);
                toast.success("Marked do-not-call");
              },
              onError: (e) =>
                toast.error("Could not mark do-not-call", e instanceof ApiError ? e.message : "Please try again."),
            },
          )
        }
      />
    </div>
  );
}

/** An admin takes the lead out of its exit stage (ADR-076). */
function ReopenDialog({
  open, onOpenChange, contact,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  contact: LeadCard;
}) {
  const toast = useToast();
  const reopen = useReopenLead();
  const [stage, setStage] = React.useState<string>("");
  const [reason, setReason] = React.useState("");
  const [problem, setProblem] = React.useState<string | null>(null);
  React.useEffect(() => {
    if (!open) return;
    setStage(contact.reopen_stage ?? "new");
    setReason("");
    setProblem(null);
  }, [open, contact.reopen_stage]);
  const exit = stageText(contact.stage).label;
  const submit = () => {
    if (!reason.trim()) {
      setProblem("Say why the lead is reopened.");
      return;
    }
    reopen.mutate(
      { leadId: contact.id, stage, reason: reason.trim() },
      {
        onSuccess: (result) => {
          onOpenChange(false);
          toast.success("Reopened", reopenSummary(result));
        },
        onError: (e) => setProblem(e instanceof ApiError ? e.message : "Could not reopen. Please try again."),
      },
    );
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent size="sm">
        <DialogHeader>
          <DialogTitle>Reopen this contact?</DialogTitle>
          <DialogDescription>
            They leave {exit} for the stage you pick, and go back on the dial lists {exit} took them off:
            each campaign carries on where it left off. Contacts with the same phone that left with them come
            back too. The reason is kept in the history.
          </DialogDescription>
        </DialogHeader>
        <DialogBody className="space-y-4">
          <Field label="Reopen into" htmlFor="reopen-stage" hint={stageText(stage).description}>
            <Select value={stage} onValueChange={setStage}>
              <SelectTrigger id="reopen-stage" aria-label="Reopen into">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {reopenStages().map((s) => (
                  <SelectItem key={s} value={s} description={s === contact.reopen_stage ? "Where they were before" : undefined}>
                    {stageText(s).label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </Field>
          <Field label="Reason" htmlFor="reopen-reason" required error={problem ?? undefined}>
            <Input
              id="reopen-reason"
              value={reason}
              maxLength={500}
              invalid={Boolean(problem)}
              placeholder="e.g. marked Not qualified by mistake"
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                setReason(e.target.value);
                setProblem(null);
              }}
            />
          </Field>
        </DialogBody>
        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button loading={reopen.isPending} onClick={submit}>Reopen</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function SetFirmDialog({
  open, onOpenChange, contact,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  contact: LeadCard;
}) {
  const toast = useToast();
  const firms = useFirms({ enabled: open });
  const update = useUpdateContact();
  const [firmId, setFirmId] = React.useState<string>("");
  React.useEffect(() => {
    if (open) setFirmId(contact.firm ? String(contact.firm.id) : "");
  }, [open, contact.firm]);
  const chosen = firmId ? Number(firmId) : null;
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent size="sm">
        <DialogHeader>
          <DialogTitle>Set firm</DialogTitle>
          <DialogDescription>
            The firm this contact belongs to. It writes the firm tag on the record and leaves a note saying who set it.
          </DialogDescription>
        </DialogHeader>
        <DialogBody>
          <Field label="Firm" htmlFor="set-firm">
            <Select value={firmId} onValueChange={setFirmId}>
              <SelectTrigger id="set-firm" aria-label="Firm">
                <SelectValue placeholder="Choose a firm…" />
              </SelectTrigger>
              <SelectContent>
                {(firms.data ?? []).map((firm) => (
                  <SelectItem key={firm.id} value={String(firm.id)}>
                    {firm.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </Field>
        </DialogBody>
        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button
            loading={update.isPending}
            disabled={chosen === null || chosen === (contact.firm?.id ?? null)}
            onClick={() =>
              chosen !== null &&
              update.mutate(
                { id: contact.id, firm_id: chosen },
                {
                  onSuccess: () => {
                    toast.success("Firm set");
                    onOpenChange(false);
                  },
                  onError: (e) => toast.error("Could not set the firm", e instanceof ApiError ? e.message : "Please try again."),
                },
              )
            }
          >
            Set firm
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/** "Due in 1 min · Cycle 1, call 2 next" after "Next:" — each part starts lowercase, and a date
 *  or a name inside a part keeps its case. */
function inSentence(line: string): string {
  return line
    .split(" · ")
    .map((part) => part.charAt(0).toLowerCase() + part.slice(1))
    .join(" · ");
}

/** Pause, resume or start the follow-up again (ADR-066). A pause is a hold on the lead's rows,
 *  written at once; starting again is a request the dialer applies at its next sweep. */
function CadenceActionDialog({
  action, onClose, contact,
}: {
  action: "pause" | "resume" | "re-enroll" | null;
  onClose: () => void;
  contact: LeadCard;
}) {
  const toast = useToast();
  const act = useLeadCadenceAction();
  const [reason, setReason] = React.useState("");
  React.useEffect(() => {
    if (action) setReason("");
  }, [action]);
  const words = {
    pause: {
      title: "Pause the follow-up?",
      description: "No cadence campaign calls this lead until somebody resumes. A reason is kept on the record.",
      confirm: "Pause",
    },
    resume: {
      title: "Resume the follow-up?",
      description: "The lead is called again from where the cadence left it.",
      confirm: "Resume",
    },
    "re-enroll": {
      title: "Start the follow-up again?",
      description: "The lead starts the cadence from the top, as a fresh lead, at the dialer's next sweep. Refused for a do-not-call lead or a signed one.",
      confirm: "Start again",
    },
  } as const;
  const w = action ? words[action] : words.pause;
  return (
    <Dialog open={action !== null} onOpenChange={(open) => !open && onClose()}>
      <DialogContent size="sm">
        <DialogHeader>
          <DialogTitle>{w.title}</DialogTitle>
          <DialogDescription>{w.description}</DialogDescription>
        </DialogHeader>
        <DialogBody>
          <Field label="Reason" htmlFor="cadence-action-reason" hint="Optional. Kept with the request and on the record.">
            <Input value={reason} onChange={(e: React.ChangeEvent<HTMLInputElement>) => setReason(e.target.value)} maxLength={500} />
          </Field>
        </DialogBody>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>Cancel</Button>
          <Button
            loading={act.isPending}
            onClick={() =>
              action &&
              act.mutate(
                { leadId: contact.id, action, reason: reason.trim() || null, idempotency_key: `${action}:${contact.id}:${Date.now()}` },
                {
                  onSuccess: (result) => {
                    onClose();
                    toast.success(
                      result.affected === 0 ? "Nothing to change" : w.confirm,
                      result.affected === 0
                        ? "No list row of this lead was in a state that action applies to."
                        : `${result.affected} list row${result.affected === 1 ? "" : "s"}.`,
                    );
                  },
                  onError: (e) => toast.error(`Could not ${w.confirm.toLowerCase()}`, e instanceof ApiError ? e.message : "Please try again."),
                },
              )
            }
          >
            {w.confirm}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function Facts({ contact }: { contact: LeadCard }) {
  const cadence = useLeadCadence(contact.id);
  const now = useNow(30_000);
  const attributes = contact.attributes ?? {};
  const shown = SOURCE_ATTRIBUTES.filter(([key]) => attributes[key] !== undefined && attributes[key] !== null && attributes[key] !== "");
  const none = <span className="text-text-muted">—</span>;
  return (
    <div className="space-y-4">
      <FollowUpCard contact={contact} cadence={cadence.data} now={now} />
      <Card>
        <CardHeader title="Identity" />
        <DescriptionList className="px-4">
          <DescriptionItem label="Name">{contactName(contact) ?? none}</DescriptionItem>
          <DescriptionItem label="Phone">
            <span className="font-mono">{formatPhoneNumber(contact.phone)}</span>
          </DescriptionItem>
          <DescriptionItem label="Email">
            {contact.email ? <span className="break-all">{contact.email}</span> : none}
          </DescriptionItem>
          <DescriptionItem label="Language">{languageName(contact.language) ?? none}</DescriptionItem>
          <DescriptionItem label="Timezone">{contact.timezone ?? <span className="text-text-muted">From the state</span>}</DescriptionItem>
          <DescriptionItem label="Residence">{contact.state ?? none}</DescriptionItem>
        </DescriptionList>
      </Card>
      <Card>
        <CardHeader title="Firm and lists" />
        <DescriptionList className="px-4">
          <DescriptionItem label="Firm">
            {contact.firm ? contact.firm.label : <span className="text-warning-11">No firm — set one, or the next import resolves its tag</span>}
          </DescriptionItem>
          <DescriptionItem label={contact.campaigns.length === 1 ? "List" : "Lists"}>
            {contact.campaigns.length === 0 ? (
              <span className="text-text-muted">On no campaign's list</span>
            ) : (
              <ul className="space-y-2">
                {contact.campaigns.map((m) => (
                  <li key={m.campaign_id}>
                    <Link to={`/campaigns/${m.campaign_id}?tab=lists`} className="hover:underline">
                      {m.campaign_name ?? "Deleted campaign"}
                    </Link>
                    <MembershipLine membership={m} contact={contact} />
                  </li>
                ))}
              </ul>
            )}
          </DescriptionItem>
          {contact.siblings && contact.siblings.length > 0 && (
            <DescriptionItem label="Same phone">
              <ul className="space-y-0.5">
                {contact.siblings.slice(0, SIBLINGS_SHOWN).map((s) => (
                  <li key={s.id}>
                    <Link to={`/contacts/${s.id}`} className="hover:underline">
                      {s.firm?.label ?? "No firm"} · {stageText(s.stage).label}
                    </Link>
                    <span className="text-xs text-text-muted"> · {formatDateTime(s.created_at).split(",").slice(0, 2).join(",")}</span>
                    {s.dnc && <Badge tone="danger" size="sm" className="ml-1">DNC</Badge>}
                  </li>
                ))}
              </ul>
              {contact.siblings.length > SIBLINGS_SHOWN && (
                <Link to={`/contacts?q=${encodeURIComponent(contact.phone)}`} className="mt-1 inline-block text-xs text-text-muted hover:text-text hover:underline">
                  and {contact.siblings.length - SIBLINGS_SHOWN} more — see all
                </Link>
              )}
            </DescriptionItem>
          )}
        </DescriptionList>
      </Card>
      <Card>
        <CardHeader title="Source" />
        <DescriptionList className="px-4">
          <DescriptionItem label="Source">
            {contact.source}
            {contact.source_ref && <span className="ml-1 break-all font-mono text-xs text-text-muted">{contact.source_ref}</span>}
          </DescriptionItem>
          {shown.map(([key, label]) => (
            <DescriptionItem key={key} label={label}>
              {Array.isArray(attributes[key]) ? (attributes[key] as unknown[]).map(String).join(", ") : String(attributes[key])}
            </DescriptionItem>
          ))}
          <DescriptionItem label="Created">{formatDateTime(contact.created_at)}</DescriptionItem>
        </DescriptionList>
      </Card>
      <Card>
        <CardHeader title="Consent" description="Immutable. What the person agreed to, and when." />
        <DescriptionList className="px-4">
          <DescriptionItem label="Captured">
            {contact.consent_captured_at ? formatDateTime(contact.consent_captured_at) : <span className="text-text-muted">No consent evidence — the dialer will not call this lead</span>}
          </DescriptionItem>
          {contact.consent_wording && (
            <DescriptionItem label="Wording">
              <span className="text-xs text-text-muted">“{contact.consent_wording}”</span>
            </DescriptionItem>
          )}
        </DescriptionList>
      </Card>
    </div>
  );
}

/** One line under the campaign's name: how the list holds the lead, and what comes next. */
function MembershipLine({ membership: m, contact }: { membership: LeadCardCampaign; contact: LeadCard }) {
  const dial = dialText(m.dial ?? null);
  const detail = dialDetail({ dial: m.dial ?? null, membership: m });
  const next = nextText({ dial: m.dial ?? null, next_at: m.next_at ?? null });
  return (
    <div className="mt-0.5 text-xs">
      <Badge tone={dial.tone} size="sm" title={dial.description}>{dial.label}</Badge>
      {detail && <span className="ml-1 text-text-muted">{detail}</span>}
      {m.last_disposition && <span className="text-text-muted"> · last: {m.last_disposition}</span>}
      {next && <span className="text-text-muted"> · {next.toLowerCase()}</span>}
      {m.list_name && <span className="text-text-muted"> · list: {m.list_name}</span>}
      {contact.dnc && !m.dial && <span className="text-text-muted"> · do not call</span>}
    </div>
  );
}

function ContactLists({ contact }: { contact: LeadCard }) {
  if (contact.campaigns.length === 0) {
    return <EmptyState title="On no campaign's list" description="Add to campaign, Hold and Release per row arrive with the appointments work." />;
  }
  return (
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead>Campaign</TableHead>
          <TableHead>Dial</TableHead>
          <TableHead>Attempts</TableHead>
          <TableHead>Last outcome</TableHead>
          <TableHead>Next</TableHead>
          <TableHead>Leaves the list</TableHead>
          <TableHead>List</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {contact.campaigns.map((m) => {
          const dial = dialText(m.dial ?? null);
          const next = nextText({ dial: m.dial ?? null, next_at: m.next_at ?? null });
          return (
            <TableRow key={m.campaign_id}>
              <TableCell>
                <Link to={`/campaigns/${m.campaign_id}?tab=lists`} className="hover:underline">
                  {m.campaign_name ?? "Deleted campaign"}
                </Link>
              </TableCell>
              <TableCell>
                <Badge tone={dial.tone} size="sm" title={dial.description}>{dial.label}</Badge>
                {m.dial === "held" && m.hold_reason && <span className="ml-1 text-xs text-text-muted">{dialDetail({ dial: m.dial, membership: m })}</span>}
              </TableCell>
              <TableCell className="tabular-nums">
                {m.attempts}
                {m.max_attempts ? <span className="text-text-subtle"> of {m.max_attempts}</span> : null}
              </TableCell>
              <TableCell className="text-xs">
                {m.last_disposition ?? <span className="text-text-subtle">—</span>}
                {m.last_attempt_at && <span className="text-text-subtle"> · {relativeTime(m.last_attempt_at)}</span>}
              </TableCell>
              <TableCell className="text-xs text-text-muted">{next ?? "—"}</TableCell>
              <TableCell className="text-xs text-text-muted">{m.retire_at ? formatDateTime(m.retire_at) : "—"}</TableCell>
              <TableCell className="text-xs text-text-muted">{m.list_name ?? (m.list_id ? `List ${m.list_id}` : "—")}</TableCell>
            </TableRow>
          );
        })}
      </TableBody>
    </Table>
  );
}

function ContactCalls({ contact }: { contact: LeadCard }) {
  const [page, setPage] = React.useState(0);
  const [openId, setOpenId] = React.useState<number | undefined>();
  const list = useContactCalls(contact.id, page);
  const rows = list.data?.items ?? [];
  return (
    <>
      {list.isPending ? (
        <TableSkeleton cols={6} rows={4} />
      ) : list.error ? (
        <ErrorState error={list.error} title="Could not load the calls" />
      ) : rows.length === 0 ? (
        <EmptyState title="No calls yet" description="Every leg the switch finished with this lead on it appears here." />
      ) : (
        <>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Started</TableHead>
                <TableHead>From</TableHead>
                <TableHead>To</TableHead>
                <TableHead>Campaign</TableHead>
                <TableHead className="text-right">Talk</TableHead>
                <TableHead>Outcome</TableHead>
                <MediaHead />
              </TableRow>
            </TableHeader>
            <TableBody>
              {rows.map((call) => (
                <CallLine key={call.id} call={call} onOpen={() => setOpenId(call.id)} />
              ))}
            </TableBody>
          </Table>
          <CallPager page={page} count={rows.length} total={list.data?.total ?? null} onChange={setPage} busy={list.isFetching} pageSize={CALLS_PAGE_SIZE} />
        </>
      )}
      <CallDetailDialog
        id={openId}
        onClose={() => setOpenId(undefined)}
        onOpenLeg={setOpenId}
      />
    </>
  );
}

function CallLine({ call, onOpen }: { call: Cdr; onOpen: () => void }) {
  const outcome = callOutcome(call);
  return (
    <TableRow
      interactive
      tabIndex={0}
      role="button"
      aria-label={`Call at ${formatDateTime(call.start_time)}`}
      onClick={onOpen}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          onOpen();
        }
      }}
    >
      <TableCell className="whitespace-nowrap text-xs text-text-muted">
        <span className="inline-flex items-center gap-1.5">
          <DirectionIcon direction={call.direction} />
          {formatDateTime(call.start_time)}
        </span>
      </TableCell>
      <TableCell className="whitespace-nowrap font-mono text-xs">{call.caller_number ? formatPhoneNumber(call.caller_number) : "—"}</TableCell>
      <TableCell className="whitespace-nowrap font-mono text-xs">{call.callee_number ? formatPhoneNumber(call.callee_number) : "—"}</TableCell>
      <TableCell className="max-w-48 truncate text-text-muted">{call.campaign_name ?? "—"}</TableCell>
      <TableCell className="text-right font-mono text-xs">{talkShown(call) ? formatDuration(call.billsec_seconds) : "—"}</TableCell>
      <TableCell>
        <Badge tone={outcome.tone} size="sm">{outcome.label}</Badge>
      </TableCell>
      <MediaCell call={call} />
    </TableRow>
  );
}
